import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getListCareProtocolOpportunitiesQueryKey,
  useDismissCareProtocolOpportunity,
  useListCareGapUploadDocuments,
} from "@elation/api-client/src/stitch-web";
import type {
  CareProtocolOpportunityListItem,
  Document,
} from "@elation/api-client/src/stitch-web/models";

import { DocumentViewer } from "@src/features/documents";
import { PatientEntity } from "@src/features/patients";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { navigationV2 } from "@src/shared/navigation/v2";

import { CareProtocolOpportunityEntity } from "../../../../../domain";
import { UploadHistoryFileNameTrigger } from "./UploadHistoryFileNameTrigger";
import {
  type CareProtocolOpportunityPatientGroup,
  groupCareProtocolOpportunitiesByPatient,
} from "./uploadHistoryGrouping";

function matchesSearchTerm({
  documents,
  group,
  searchTerm,
}: {
  documents: Document[];
  group: CareProtocolOpportunityPatientGroup;
  searchTerm: string;
}) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) return true;

  const searchableText = [
    PatientEntity.displayName(group.patient),
    group.patient.dob,
    ...group.items.map((item) => item.description),
    ...documents.map((document) => document.fileName),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchableText.includes(normalizedSearchTerm);
}

function PatientUploadRow({
  date,
  defaultOpen,
  documents,
  group,
  uploadedAt,
}: {
  date: string;
  defaultOpen: boolean;
  documents: Document[];
  group: CareProtocolOpportunityPatientGroup;
  uploadedAt: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const patientDocuments = documents.filter(
    (document) => document.patientId === group.patient.id,
  );
  const dismiss = useDismissCareProtocolOpportunity({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getListCareProtocolOpportunitiesQueryKey(),
        });
      },
    },
  });
  const primaryDocument = patientDocuments[0];

  return (
    <li>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="grid grid-cols-[minmax(150px,1.15fr)_minmax(160px,1.1fr)_120px_80px_72px] items-center gap-3 px-3 py-2.5">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-auto min-w-0 justify-start gap-2 rounded-[8px] px-0 py-0 text-left font-normal shadow-none hover:bg-transparent hover:text-foreground"
            >
              <ChevronRight
                className={`size-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                aria-hidden="true"
              />
              <VerticalFlexbox className="min-w-0 flex-1">
                <span className="truncate text-[13px] font-semibold text-surface-foreground">
                  {PatientEntity.displayName(group.patient) ??
                    "Unknown patient"}
                </span>
                <span className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                  {[
                    group.patient.dob.replaceAll("-", "/"),
                    `${group.patient.age}${PatientEntity.sexAbbreviation(group.patient.sex) ?? ""}`,
                  ].join(" · ")}
                </span>
              </VerticalFlexbox>
            </Button>
          </CollapsibleTrigger>

          <div className="min-w-0">
            {primaryDocument ? (
              <DocumentViewer
                documentId={primaryDocument.id}
                fileName={primaryDocument.fileName}
                uploadedAtLabel={CareProtocolOpportunityEntity.uploadTimeLabel(
                  primaryDocument.uploadedAt,
                )}
                triggerTooltip={primaryDocument.fileName}
                trigger={
                  <UploadHistoryFileNameTrigger
                    fileName={primaryDocument.fileName}
                  />
                }
              />
            ) : (
              <span className="font-mono text-[10.5px] text-muted-foreground">
                {date}
              </span>
            )}
          </div>

          <span className="font-mono text-[10.5px] text-muted-foreground">
            {CareProtocolOpportunityEntity.uploadTimeLabel(uploadedAt)}
          </span>
          <span className="font-mono text-[10.5px] text-muted-foreground">
            {CareProtocolOpportunityEntity.itemCountLabel(group.items)}
          </span>
          <Button
            type="button"
            variant="ghost"
            className="h-7 justify-end rounded-[7px] px-0 text-[12px] font-semibold text-primary hover:bg-transparent hover:text-primary"
            onClick={() =>
              navigate(navigationV2.patientDetail(String(group.patient.id)), {
                state: {
                  source: "v2 upload history",
                  from: navigationV2.documents(),
                },
              })
            }
          >
            View
          </Button>
        </div>
        <CollapsibleContent>
          <VerticalFlexbox className="gap-2 bg-muted/20 px-4 py-3">
            {patientDocuments.length > 1 ? (
              <VerticalFlexbox className="gap-1">
                {patientDocuments.slice(1).map((document) => (
                  <DocumentViewer
                    key={document.id}
                    documentId={document.id}
                    fileName={document.fileName}
                    uploadedAtLabel={CareProtocolOpportunityEntity.uploadTimeLabel(
                      document.uploadedAt,
                    )}
                  />
                ))}
              </VerticalFlexbox>
            ) : null}
            <VerticalFlexbox className="gap-1">
              {group.items.map((item) => (
                <HorizontalFlexbox
                  key={item.id}
                  alignItems="center"
                  className="group gap-[9px] rounded-[8px] px-2 py-[5px] hover:bg-muted/30"
                >
                  <span
                    className="size-[5px] shrink-0 rounded-full bg-primary"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 text-[12.5px] text-surface-foreground">
                    {item.description}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-[26px] shrink-0 rounded-[6px] text-muted-foreground hover:bg-background"
                    aria-label={`Remove ${item.description}`}
                    disabled={dismiss.isPending}
                    onClick={() => dismiss.mutate({ id: item.id })}
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </Button>
                </HorizontalFlexbox>
              ))}
            </VerticalFlexbox>
          </VerticalFlexbox>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}

export function CareProtocolOpportunityUploadGroup({
  date,
  defaultOpen = false,
  isVisible,
  items,
  shouldPollForDocuments,
  searchTerm = "",
  uploadedAt,
}: {
  date: string;
  defaultOpen?: boolean;
  isVisible: boolean;
  items: CareProtocolOpportunityListItem[];
  shouldPollForDocuments: boolean;
  searchTerm?: string;
  uploadedAt: string;
}) {
  const documentsQuery = useListCareGapUploadDocuments(
    { date },
    {
      query: {
        refetchInterval: (query) => {
          if (!isVisible) return false;
          const documents = query.state.data?.data.documents ?? [];
          if (documents.length === 0) {
            return shouldPollForDocuments ? 5_000 : false;
          }
          return documents.some((document) => document.patientId == null)
            ? 5_000
            : false;
        },
      },
    },
  );
  const documents = documentsQuery.data?.data.documents ?? [];
  const patientGroups = useMemo(
    () => groupCareProtocolOpportunitiesByPatient(items),
    [items],
  );
  const visiblePatientGroups = patientGroups.filter((group) =>
    matchesSearchTerm({ documents, group, searchTerm }),
  );

  return (
    <Fragment>
      {visiblePatientGroups.map((group) => (
        <PatientUploadRow
          key={`${date}-${group.patient.id}`}
          date={date}
          defaultOpen={defaultOpen}
          documents={documents}
          group={group}
          uploadedAt={uploadedAt}
        />
      ))}
    </Fragment>
  );
}
